import { ReactNode, useState, useCallback, useEffect, useMemo } from 'react';
import { Editor } from '@tiptap/core';
import { usePopper } from 'react-popper';
import { nanoid } from 'nanoid';

import * as Styled from './richtext-toolbar.style';
import { spacing8 } from 'approot/shared/styles/spacing.style';
import { ACTIVITY_BUILDER_TOOLBAR_ID } from 'approot/activity-builder/activity-builder.constants';
import { useDropdownTrap } from 'approot/shared/ally/keyboard.hooks';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import IconDropdown from 'approot/shared/icons/icon-dropdown';

type Props = {
  editor: Editor;
  buttonElement: ReactNode;
  dropdownContents: ReactNode;
  padding?: number;
  ariaLabel?: string;
  tooltip?: string;
  isRow?: boolean;
};

const RichtextToolbarDropdown = ({
  editor,
  buttonElement,
  dropdownContents,
  padding,
  ariaLabel,
  tooltip,
  isRow,
}: Props) => {
  const [isDropdownActive, setIsDropdownActive] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top-start'],
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, spacing8],
        },
      },
    ],
  });

  const handleClose = useCallback(
    (e: Event) => {
      const toolbarParent = document.getElementById(
        ACTIVITY_BUILDER_TOOLBAR_ID
      );

      if (!isDropdownActive || !toolbarParent) return;

      const target = e.target as HTMLElement;

      const clickedWithinToolbar = toolbarParent.contains(target);
      const clickedSelf = target === referenceElement;

      if (clickedSelf) {
        // the click handler will cover this case
        return;
      }

      if (clickedWithinToolbar) {
        setIsDropdownActive(false);
      } else {
        setIsDropdownActive(false);
        editor.chain().blur();
      }
    },
    [isDropdownActive, referenceElement, editor]
  );

  useEffect(() => {
    document.addEventListener('click', handleClose, false);
    return () => {
      document.removeEventListener('click', handleClose, false);
    };
  }, [handleClose]);

  useDropdownTrap(popperElement, referenceElement, () =>
    setIsDropdownActive(false)
  );

  const id = useMemo(() => nanoid(), []);

  const button = (
    <Styled.RichtextDropdownButton
      aria-haspopup="listbox"
      aria-expanded={isDropdownActive}
      aria-label={ariaLabel}
      data-dropdown-trigger={true}
      data-dropdown-container-id={id}
      ref={setReferenceElement}
      onClick={() => {
        setIsDropdownActive(!isDropdownActive);
      }}
    >
      {buttonElement}
      <Styled.RichtextDropdownIcon>
        <IconDropdown width={12} />
      </Styled.RichtextDropdownIcon>
    </Styled.RichtextDropdownButton>
  );

  return (
    <>
      {tooltip ? (
        <TooltipWrapper hoverText={tooltip}>{button}</TooltipWrapper>
      ) : (
        button
      )}

      {isDropdownActive && (
        <Styled.RichtextDropdown
          id={id}
          ref={setPopperElement}
          role="listbox"
          style={styles.popper}
          tabIndex={-1}
          $padding={padding}
          $isRow={isRow}
          {...attributes.popper}
        >
          {dropdownContents}
        </Styled.RichtextDropdown>
      )}
    </>
  );
};

export default RichtextToolbarDropdown;
